'use client';

import Image from 'next/image';
import { useState } from 'react';
import { Eye, EyeOff } from 'lucide-react';
import { ApiError } from '@/lib/api';
import { useAuth } from '@/components/auth/AuthProvider';

export function LoginScreen({ notice }: { notice?: string }) {
  const { login } = useAuth();
  const [username, setUsername] = useState('');
  const [password, setPassword] = useState('');
  const [showPassword, setShowPassword] = useState(false);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const submit = (event: React.FormEvent): void => {
    event.preventDefault();
    setBusy(true);
    setError(null);
    login({ username: username.trim(), password })
      .catch((err: unknown) => {
        setError(err instanceof ApiError ? err.displayMessage : 'Gagal masuk, coba lagi.');
      })
      .finally(() => setBusy(false));
  };

  const field =
    'w-full rounded-md border-[1.5px] border-white/15 bg-white/5 px-3.5 py-2.5 text-[14px] text-clickwhite outline-none focus:border-lime';

  return (
    <main className="grid min-h-dvh place-items-center bg-charcoal px-6">
      <div className="w-full max-w-[380px]">
        <div className="mb-7 flex items-center gap-2.5">
          <Image
            src="/brand/ci-mark.webp"
            unoptimized
            alt=""
            width={129}
            height={192}
            priority
            className="h-10 w-auto shrink-0"
          />
          <span className="font-display text-lg text-clickwhite">CIPTA IMAJI</span>
        </div>

        <h1 className="font-display text-[2.2rem] leading-none text-clickwhite">Panel admin</h1>
        <p className="mt-2 mb-6 text-[13px] leading-relaxed text-white/80">
          {notice ?? 'Masuk dengan akun yang punya akses pengelolaan.'}
        </p>

        {error ? (
          <p
            role="alert"
            className="mb-4 rounded-r-md border-l-4 border-redline bg-white/5 px-3 py-2.5 text-[12.5px] text-clickwhite"
          >
            {error}
          </p>
        ) : null}

        <form onSubmit={submit} noValidate>
          <label
            htmlFor="username"
            className="mb-1.5 block text-[12.5px] font-bold text-clickwhite"
          >
            Username
          </label>
          <input
            id="username"
            type="text"
            autoComplete="username"
            autoCapitalize="none"
            spellCheck={false}
            value={username}
            onChange={(e) => setUsername(e.target.value)}
            className={`${field} mb-3.5`}
          />

          <label
            htmlFor="password"
            className="mb-1.5 block text-[12.5px] font-bold text-clickwhite"
          >
            Kata sandi
          </label>
          <div className="relative mb-5">
            <input
              id="password"
              type={showPassword ? 'text' : 'password'}
              autoComplete="current-password"
              value={password}
              onChange={(e) => setPassword(e.target.value)}
              className={`${field} pr-11`}
            />
            <button
              type="button"
              onClick={() => setShowPassword((v) => !v)}
              aria-label={showPassword ? 'Sembunyikan kata sandi' : 'Tampilkan kata sandi'}
              aria-pressed={showPassword}
              className="absolute inset-y-0 right-0 grid w-11 place-items-center text-white/85 transition-colors hover:text-lime"
            >
              {showPassword ? (
                <EyeOff className="size-[18px]" strokeWidth={2} aria-hidden />
              ) : (
                <Eye className="size-[18px]" strokeWidth={2} aria-hidden />
              )}
            </button>
          </div>

          <button
            type="submit"
            disabled={busy}
            className="w-full rounded-md bg-lime px-7 py-3.5 text-sm font-bold text-charcoal disabled:opacity-40"
          >
            {busy ? 'Memproses…' : 'Masuk'}
          </button>
        </form>
      </div>
    </main>
  );
}
